import {
    GridRowParams,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import {
    useCallback, useMemo, MutableRefObject, memo,
} from 'react'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

import {
    SkyBaseCreateButton,
    ExtendedGridApiPro,
} from 'app/shared-components/SkyBaseDataGrid'
import SkyNetDataGrid from 'app/shared-components/SkyNetDataGrid'
import {
    WithId,
} from 'app/types/common.types'
import useAutoOpenForm from './hooks/useAutoOpenForm'
import useExpandableActions from './hooks/useExpandableActions'

import useStyles from './SkyNetDataGridExpandable.styles'
import {
    SkyNetDataGridExpandableProps,
} from './SkyNetDataGridExpandable.types'
import useAllowedActions from './hooks/useAllowedActions'
import StableHeightWrapper from './components/StableHeightWrapper'

const MIN_DETAIL_PANEL_HEIGHT = 400

export default function SkyNetDataGridExpandable({
    columns,
    ActionComponents,
    autoOpenForm = true,
    customDataSource,
    toolbarProps: customToolbarProps,
    featuresLists,
    gridApiRef,
    key = 'id',
    slots,
    persistentKey,
    detailPanelHeight,
    ...props
}: Readonly<SkyNetDataGridExpandableProps>) {
    const {
        classes,
    } = useStyles()

    const newApiRef = useGridApiRef<ExtendedGridApiPro>()
    const apiRef: MutableRefObject<ExtendedGridApiPro> = gridApiRef || newApiRef

    const getDetailPanelHeight = useCallback(() => {
        return detailPanelHeight ?? 'auto'
    }, [detailPanelHeight])

    useAutoOpenForm(apiRef, autoOpenForm)

    const allowedActions = useAllowedActions({
        actionComponents: ActionComponents,
    })

    // Create memoized version of Update component once - prevents re-renders when id stays the same
    const MemoizedUpdateComponent = useMemo(() => {
        const Component = allowedActions?.Update?.component

        if (!Component) return null

        return memo(Component, (prevProps, nextProps) => {
            return prevProps.id === nextProps.id
        })
    }, [allowedActions?.Update?.component])

    const {
        action,
        actionId,
        clearAction,
        collapse,
        collapseAll,
        copy,
        create,
        expand,
        refetch,
    } = useExpandableActions({
        apiRef, dataSource: customDataSource,
    })

    const getOnSuccessUpdate = useCallback((id: number | string) => {
        return async ({
            toCollapse = false,
        }: { toCollapse: boolean }) => {
            await refetch()
            if (toCollapse) {
                collapse(id)
                return
            }
            expand(id)
        }
    }, [
        collapse,
        expand,
        refetch,
    ])

    const onCopyClick = useCallback((id: number | string) => {
        collapseAll()
        copy(id)
    }, [
        collapseAll,
        copy,
    ])

    const getUpdateForm = useCallback((row: GridRowParams) => {
        const id = row?.[key]

        if (!id || !MemoizedUpdateComponent) return null

        return (
            <div className={classes.detailsViewContainer}>
                <StableHeightWrapper minHeight={MIN_DETAIL_PANEL_HEIGHT}>
                    <MemoizedUpdateComponent
                        id={id}
                        onSuccess={allowedActions?.Update?.onSuccess || getOnSuccessUpdate(id)}
                        onCopy={() => {
                            onCopyClick(id)
                        }}
                        onCancel={() => {
                            collapse(id)
                        }}
                    />
                </StableHeightWrapper>
            </div>
        )
    }, [
        MemoizedUpdateComponent,
        allowedActions?.Update?.onSuccess,
        classes.detailsViewContainer,
        collapse,
        getOnSuccessUpdate,
        key,
        onCopyClick,
    ])

    const onSuccessAction = useCallback(async ({
        id,
    }: WithId<string | number>) => {
        clearAction()
        await refetch()
        expand(id)
    }, [
        clearAction,
        expand,
        refetch,
    ])

    const onCancelAction = useCallback(() => {
        clearAction()
        expand(actionId)
    }, [
        actionId,
        clearAction,
        expand,
    ])

    const renderAction = useCallback(() => {
        if (!action) return null

        const ActionComponent = allowedActions?.[action]?.component

        if (!ActionComponent) return null

        return (
            <div className={classes.actionFormContainer}>
                <ActionComponent
                    id={actionId}
                    onCancel={onCancelAction}
                    onSuccess={allowedActions?.[action]?.onSuccess || onSuccessAction}
                />
            </div>
        )
    }, [
        allowedActions,
        action,
        actionId,
        classes.actionFormContainer,
        onCancelAction,
        onSuccessAction,
    ])

    const defaultSlots = useMemo(() => {
        return {
            detailPanelExpandIcon: KeyboardArrowRightIcon,
            detailPanelCollapseIcon: KeyboardArrowDownIcon,
            ...slots,
        }
    }, [slots])

    const toolbarProps = useMemo(() => {
        let toolbarControls = customToolbarProps?.toolbarControls || []

        if (allowedActions?.Create?.component && !action && !actionId) {
            toolbarControls = [
                <SkyBaseCreateButton onClick={create} />,
                ...toolbarControls,
            ]
        }

        return {
            ...customToolbarProps,
            toolbarControls,
        }
    }, [
        allowedActions?.Create,
        action,
        actionId,
        create,
        customToolbarProps,
    ])

    return (
        <>
            {renderAction()}
            <SkyNetDataGrid
                {...props} /* eslint-disable-line react/jsx-props-no-spreading */
                gridApiRef={apiRef}
                persistentKey={persistentKey}
                columns={columns}
                customDataSource={customDataSource}
                toolbarProps={toolbarProps}
                featuresLists={featuresLists}
                getDetailPanelContent={
                    allowedActions?.Update?.component ? getUpdateForm : undefined
                }
                slots={defaultSlots}
                getDetailPanelHeight={getDetailPanelHeight}
                classNames={{
                    container: classes.expandableDataGridOverwrites,
                }}
            />
        </>
    )
}
